import type { ReactNode } from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { cn } from '@/lib/cn';
import { ABAS, abaDe, RAIZ_DE_MUDANCAS } from '@/features/changes/navegacao';

/**
 * Casca do calendário de mudanças: título, abas e o resumo da aba corrente.
 *
 * As abas são **rotas**, não estado de componente — pelo mesmo motivo da
 * administração e dos relatórios: `/mudancas/congelamentos` precisa ser um
 * endereço que se manda por mensagem na véspera da reunião do comitê, se
 * recarrega e se alcança pelo botão "voltar".
 */
export function MudancasLayout({ children }: { children?: ReactNode }) {
  const pathname = useRouterState({ select: (estado) => estado.location.pathname });
  const atual = abaDe(pathname.split('/').filter(Boolean).at(1) ?? '');

  return (
    <div className="mx-auto max-w-[1400px] space-y-4">
      <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
        <div>
          <h1 className="text-base font-semibold">Mudanças</h1>
          {atual ? (
            <p className="mt-0.5 text-xs text-muted-foreground">{atual.resumo}</p>
          ) : null}
        </div>
      </header>

      <nav aria-label="Calendário de mudanças" className="border-b border-border">
        <ul className="-mb-px flex flex-wrap gap-1">
          {ABAS.map((aba) => {
            const destino = `${RAIZ_DE_MUDANCAS}/${aba.caminho}`;
            const ativa = pathname === destino || pathname.startsWith(`${destino}/`);
            const Icone = aba.icone;
            return (
              <li key={aba.caminho}>
                <Link
                  to={destino}
                  aria-current={ativa ? 'page' : undefined}
                  className={cn(
                    'inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium',
                    'transition-colors focus-visible:shadow-glow focus-visible:outline-none',
                    ativa
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
                  )}
                >
                  <Icone className="h-4 w-4" aria-hidden />
                  {aba.rotulo}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {children ?? <Outlet />}
    </div>
  );
}
